/**
 * The work timeline: every stretch of an agent's work on the project, who or what set it going,
 * what it was against (task, plan, roadmap item) and how it ended. Each repository's workbench
 * posts its runs; the project reads them all.
 */

import { RUN_OUTCOME_TEXT, RUN_TRIGGER_TEXT, runDurationMs, runMatches, runTotals, type RunFilter, type WorkRun } from '@aioe/shared';
import { Clock } from '@gravity-ui/icons';
import { useMemo, useState } from 'react';

import { routes } from '../api/client.ts';
import { useAgentName } from '../agentNames.ts';
import { timeAgo, useLoad, useNow } from '../hooks.ts';
import { useMoney } from '../money.tsx';
import { SubjectName } from './SubjectPicker.tsx';
import { ChoiceSelect, Empty, ErrorNote, ListBlock, ListGroup, ListRow, Stat, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { CopyRunIdButton } from './ui/RunId.tsx';

/** A ListBox id for the empty answer (none, any, all): an empty string is not a reliable id. */
const NONE = '__none';

type Window = 'day' | 'week' | 'month';
const WINDOWS: Record<Window, { label: string; ms: number }> = { day: { label: 'Last 24 hours', ms: 86_400_000 }, week: { label: 'Last 7 days', ms: 7 * 86_400_000 }, month: { label: 'Last 30 days', ms: 30 * 86_400_000 } };

export const duration = (ms: number): string => (ms < 60_000 ? `${Math.round(ms / 1000)} s` : ms < 3_600_000 ? `${Math.round(ms / 60_000)} min` : `${(ms / 3_600_000).toFixed(1)} h`);

export function ProjectRuns({ projectId }: { projectId: string }) {
  const { format } = useMoney();
  const money = (usd: number) => format(usd, { compact: true });
  const tone = VIEW_TONES.projects!;
  const [win, setWin] = useState<Window>('week');
  // The shared clock: the window slides with it, and a run still going counts its time up to now.
  const now = useNow();
  // To the minute, so the query (and so the load) changes no more than the clock does.
  const from = new Date(Math.floor((now - WINDOWS[win].ms) / 60_000) * 60_000).toISOString();
  const data = useLoad(() => routes.projectRuns(projectId, { from, limit: 1000 }), [projectId, from], 30_000);
  const [filter, setFilter] = useState<RunFilter>({});
  const runs = useMemo(() => data.data?.runs ?? [], [data.data]);
  const people = data.data?.people ?? {};
  const shown = useMemo(() => runs.filter((r) => runMatches(r, filter)).sort((a, b) => b.startedAt.localeCompare(a.startedAt)), [runs, filter]);
  const totals = runTotals(shown, now);
  const options = (pick: (r: WorkRun) => string | undefined) => [...new Set(runs.map(pick).filter((x): x is string => !!x))].sort();
  const agentName = useAgentName();
  const who = (r: WorkRun) => (r.trigger === 'person' && r.by?.principal ? <>asked by {people[r.by.principal]?.name ?? <SubjectName id={r.by.principal} />}</> : r.trigger === 'agent' ? <>handed over by {agentName(r.by?.actor) ?? 'an agent'}</> : <>{r.by?.routine?.name ? `routine ${r.by.routine.name}` : 'automation'}</>);
  const against = (r: WorkRun) => [r.task ? `task ${r.task.title ?? r.task.id}` : undefined, r.plan ? `plan ${r.plan.name ?? r.plan.id}${r.plan.step ? ` · ${r.plan.step}` : ''}` : undefined, r.roadmap?.length ? `roadmap ${r.roadmap.map((x) => x.id).join(', ')}` : undefined].filter(Boolean).join(' · ');
  return (
    <>
      <div className="art-stats">
        <Stat tone={tone} icon={<Clock />} label="Runs" value={data.data ? totals.runs : undefined} detail={`${totals.agents} ${totals.agents === 1 ? 'agent' : 'agents'} · ${WINDOWS[win].label.toLowerCase()}`} />
        <Stat tone={tone} icon={<Clock />} label="Run time" value={data.data ? duration(totals.runTimeMs) : undefined} detail={shown.some((r) => !r.endedAt) ? `${shown.filter((r) => !r.endedAt).length} still going` : 'Nothing running now'} />
        <Stat tone={tone} icon={<Clock />} label="Tokens" value={data.data ? totals.tokens : undefined} detail={totals.costUsd ? money(totals.costUsd) : 'No cost recorded'} />
        <Stat tone={tone} icon={<Clock />} label="Did not finish" value={data.data ? totals.failed + totals.cancelled : undefined} detail={`${totals.failed} failed · ${totals.cancelled} cancelled`} needsYou={totals.failed > 0} />
      </div>
      <ListGroup
        title="Timeline"
        count={data.data ? shown.length : undefined}
        description={data.data?.truncated ? 'The newest 1000 are shown; shorten the window to see everything.' : 'Newest first: each stretch of an agent’s work, who set it going and how it ended.'}
        actions={
          <>
            <ChoiceSelect label="Window" value={win} options={Object.fromEntries((Object.keys(WINDOWS) as Window[]).map((k) => [k, WINDOWS[k].label])) as Record<Window, string>} onChange={setWin} />
            <ChoiceSelect
              label="Team"
              value={filter.teamId ?? NONE}
              options={{ [NONE]: 'Every team', ...Object.fromEntries(options((r) => r.teamId).map((t) => [t, runs.find((r) => r.teamId === t)?.teamName ?? t])) }}
              onChange={(v) => setFilter({ ...filter, teamId: v === NONE ? undefined : v })}
            />
            <ChoiceSelect
              label="Agent"
              value={filter.agent ?? NONE}
              options={{ [NONE]: 'Every agent', ...Object.fromEntries(options((r) => r.agent).map((a) => [a, a])) }}
              onChange={(v) => setFilter({ ...filter, agent: v === NONE ? undefined : v })}
            />
          </>
        }
        empty={data.data && shown.length === 0 && !data.error ? <Empty tone={tone} icon={<Clock />} compact title="No runs in this window" body="Each stretch of an agent's work is recorded by the workbench it ran on and shows here, with what it was against and how it ended." /> : undefined}
      >
        {data.error ? (
          <ListBlock>
            <ErrorNote error={data.error} />
          </ListBlock>
        ) : null}
        {!data.data && !data.error ? <ListBlock className="text-sm text-muted">Reading the runs…</ListBlock> : null}
        {shown.map((r) => (
          <ListRow
            key={`${r.rootId}/${r.id}`}
            title={
              <>
                {r.agent}
                <span className="font-normal text-muted">
                  {' '}
                  · {r.teamName ?? r.teamId} · {who(r)}
                </span>
              </>
            }
            description={
              <>
                {r.title ? <span className="block truncate">{r.title}</span> : null}
                <span className="block">
                  {[r.rootId, against(r) || undefined, r.tokens ? `${(r.tokens.input + r.tokens.output).toLocaleString()} tokens` : undefined, r.model, r.sandboxed ? 'in a sandbox' : undefined].filter(Boolean).join(' · ')}
                  {' · '}
                  <span className="tabular-nums">{duration(runDurationMs(r, now))}</span>
                  {' · '}
                  <span title={RUN_TRIGGER_TEXT[r.trigger]}>{timeAgo(r.startedAt)}</span>
                </span>
              </>
            }
            status={r.endedAt ? <StatusMark state={r.outcome === 'ok' ? 'connected' : r.outcome === 'cancelled' ? 'idle' : 'error'} label={RUN_OUTCOME_TEXT[r.outcome ?? 'ok']} /> : <StatusMark state="working" label="Running" />}
            control={<CopyRunIdButton runId={r.id} />}
            compactControl
          />
        ))}
      </ListGroup>
      <ProfileSummary runs={shown} />
    </>
  );
}

const PROFILE_WORDS: Record<'lean' | 'standard' | 'full', string> = { lean: 'Lean', standard: 'Standard', full: 'Full' };

/**
 * Runs by context-budget profile (workbench D272 part 3): how many, how many ran out of room, and how often the
 * context was tidied. Counted on the work run, which carries the tidy-ups made just after a turn; a tidy-up counts
 * every request shortened to fit, so a long run on a full window shows many without having lost anything.
 */
function ProfileSummary({ runs }: { runs: WorkRun[] }) {
  const rows = (['lean', 'standard', 'full'] as const)
    .map((p) => {
      const of = runs.filter((r) => r.contextProfile === p);
      const overflowed = of.filter((r) => r.overflowed).length;
      const tidied = of.reduce((n, r) => n + (r.compactions ?? 0), 0);
      return { p, runs: of.length, overflowed, perRun: of.length ? tidied / of.length : 0 };
    })
    .filter((r) => r.runs > 0);
  if (!rows.length) return null;
  return (
    <ListGroup title="Context budget" description="By the profile each run used. A tidy-up is a request shortened to fit, not lost work.">
      {rows.map((r) => (
        <ListRow
          key={r.p}
          title={PROFILE_WORDS[r.p]}
          description={
            <span className="tabular-nums">
              {r.runs} {r.runs === 1 ? 'run' : 'runs'} · {r.overflowed ? `${r.overflowed} ran out of room (${Math.round((r.overflowed / r.runs) * 100)}%)` : 'none ran out of room'} · {r.perRun.toFixed(1)} tidy-ups a run
            </span>
          }
        />
      ))}
    </ListGroup>
  );
}
